<template>
  <div v-if="inquiryState.visible" class="inquiry-modal" @click.self="closeInquiry">
    <div class="inquiry-box">
      <button class="inquiry-close" @click="closeInquiry">×</button>

      <div class="inquiry-header">
        <h2>{{ $t('inquiry.title') }}</h2>
        <p>{{ $t('inquiry.subtitle') }}</p>
      </div>

      <form class="inquiry-form" @submit.prevent="submit">
        <div class="form-row">
          <input v-model="form.name" class="form-input" :placeholder="$t('inquiry.name') + ' *'" required>
          <input v-model="form.phone" class="form-input" :placeholder="$t('inquiry.phone') + ' *'" required>
        </div>
        <div class="form-row">
          <input v-model="form.company" class="form-input" :placeholder="$t('inquiry.company')">
          <input v-model="form.subject" class="form-input" :placeholder="$t('inquiry.subject')">
        </div>
        <textarea v-model="form.content" class="form-textarea" :placeholder="$t('inquiry.content') + ' *'" rows="4" required></textarea>
        <button type="submit" class="form-submit" :disabled="submitting">
          {{ submitting ? $t('inquiry.submitting') : $t('inquiry.submit') }}
        </button>
        <p v-if="submitted" class="submit-success">✓ {{ $t('inquiry.success') }}</p>
        <p v-if="error" class="submit-error">✗ {{ error }}</p>
      </form>
    </div>
  </div>
</template>

<script>
import { reactive, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { inquiryState, closeInquiry } from '../utils/inquiry'
import { submitInquiryApi } from '../api'

export default {
  name: 'InquiryModal',
  setup() {
    const { t } = useI18n()
    const submitting = ref(false)
    const submitted = ref(false)
    const error = ref('')
    const form = reactive({ name: '', phone: '', company: '', subject: '', content: '' })

    const submit = async () => {
      submitting.value = true
      submitted.value = false
      error.value = ''
      try {
        await submitInquiryApi(form)
        submitted.value = true
        Object.assign(form, { name: '', phone: '', company: '', subject: '', content: '' })
        setTimeout(closeInquiry, 1500)
      } catch (e) {
        error.value = t('inquiry.fail')
      } finally {
        submitting.value = false
      }
    }

    return {
      inquiryState,
      closeInquiry,
      form,
      submitting,
      submitted,
      error,
      submit
    }
  }
}
</script>

<style scoped>
.inquiry-modal {
  position: fixed;
  inset: 0;
  z-index: 2000;
  background: rgba(6, 22, 42, 0.7);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  animation: modalFade 0.25s ease;
}

.inquiry-box {
  background: #ffffff;
  border-radius: 16px;
  width: 100%;
  max-width: 560px;
  padding: 36px 32px 28px;
  position: relative;
  box-shadow: 0 24px 64px rgba(0, 0, 0, 0.25);
  animation: modalPop 0.35s cubic-bezier(0.16, 1, 0.3, 1);
}

.inquiry-close {
  position: absolute;
  top: 14px;
  right: 18px;
  font-size: 28px;
  background: none;
  border: none;
  color: #9e9e9e;
  cursor: pointer;
  line-height: 1;
  transition: color 0.2s, transform 0.2s;
}

.inquiry-close:hover {
  color: #2196f3;
  transform: rotate(90deg);
}

.inquiry-header {
  text-align: center;
  margin-bottom: 24px;
}

.inquiry-header h2 {
  font-size: 26px;
  color: #0b4f8c;
  margin: 0 0 8px;
  background: linear-gradient(90deg, #0b4f8c, #2196f3);
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
}

.inquiry-header p {
  font-size: 13px;
  color: #9e9e9e;
  margin: 0;
}

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  margin-bottom: 12px;
}

.form-input,
.form-textarea {
  width: 100%;
  border: 1px solid #dcdfe6;
  border-radius: 8px;
  padding: 12px 14px;
  font-size: 14px;
  color: #1a1a1a;
  transition: border-color 0.2s, box-shadow 0.2s;
  box-sizing: border-box;
  font-family: inherit;
}

.form-input:focus,
.form-textarea:focus {
  outline: none;
  border-color: #2196f3;
  box-shadow: 0 0 0 3px rgba(33, 150, 243, 0.12);
}

.form-textarea {
  resize: vertical;
  margin-bottom: 16px;
}

.form-submit {
  width: 100%;
  background: linear-gradient(90deg, #0b4f8c 0%, #2196f3 100%);
  background-size: 150% 100%;
  color: #ffffff;
  border: none;
  border-radius: 8px;
  padding: 13px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: background-position 0.4s, transform 0.2s, box-shadow 0.2s;
}

.form-submit:hover:not(:disabled) {
  background-position: 100% 0;
  transform: translateY(-2px);
  box-shadow: 0 8px 20px rgba(33, 150, 243, 0.35);
}

.form-submit:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.submit-success {
  text-align: center;
  color: #67c23a;
  font-size: 14px;
  margin-top: 14px;
  animation: successIn 0.3s ease;
}

.submit-error {
  text-align: center;
  color: #f56c6c;
  font-size: 13px;
  margin-top: 10px;
}

@keyframes modalFade {
  from { opacity: 0; }
  to { opacity: 1; }
}

@keyframes modalPop {
  from { transform: scale(0.9) translateY(24px); opacity: 0; }
  to { transform: scale(1) translateY(0); opacity: 1; }
}

@keyframes successIn {
  from { opacity: 0; transform: translateY(-6px); }
  to { opacity: 1; transform: translateY(0); }
}

@media (max-width: 520px) {
  .form-row {
    grid-template-columns: 1fr;
  }
  .inquiry-box {
    max-width: 100%;
    height: 100%;
    border-radius: 0;
    padding: 28px 20px calc(20px + env(safe-area-inset-bottom, 0px));
    overflow-y: auto;
  }
}
</style>
